/**
 * Dashboard Admin - Métriques & Performance
 * 
 * Affiche métriques agrégées uniquement (pas de détails users).
 * Conforme WCAG 2.1 AA.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiBase, getToken } from '../lib/utils';
import PlatformStats from '../components/admin/PlatformStats';
import UsersSummary from '../components/admin/UsersSummary';
import ActivityChart from '../components/admin/ActivityChart';
import StorageBreakdown from '../components/admin/StorageBreakdown';

export default function AdminMetricsPage() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('7d');

  // Récupérer métriques platform
  const { data: platformMetrics, isLoading: loadingPlatform } = useQuery({
    queryKey: ['admin', 'platform-metrics'],
    queryFn: async () => {
      const token = getToken();
      const response = await fetch(`${getApiBase()}/admin/metrics/platform`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch platform metrics');
      return response.json();
    },
    refetchInterval: 30000, // Refresh toutes les 30s
  });

  // Récupérer métriques users
  const { data: usersMetrics, isLoading: loadingUsers } = useQuery({
    queryKey: ['admin', 'users-metrics'],
    queryFn: async () => {
      const token = getToken();
      const response = await fetch(`${getApiBase()}/admin/metrics/users`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch users metrics');
      return response.json();
    },
    refetchInterval: 60000, // Refresh toutes les 60s
  });

  // Récupérer storage breakdown
  const { data: storageData, isLoading: loadingStorage } = useQuery({
    queryKey: ['admin', 'storage-breakdown'],
    queryFn: async () => {
      const token = getToken();
      const response = await fetch(`${getApiBase()}/admin/metrics/storage`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch storage breakdown');
      return response.json();
    },
    refetchInterval: 300000, // Refresh toutes les 5min
  });

  const isLoading = loadingPlatform || loadingUsers || loadingStorage;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" aria-hidden="true" />
        <span className="sr-only">Chargement du dashboard...</span>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background gradient */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800 mb-4"
            aria-label="Retour au dashboard admin"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Retour
          </button>
          <h1 className="text-3xl font-bold text-zinc-900 dark:text-white">Métriques & Performance</h1>
          <p className="text-zinc-600 dark:text-zinc-400 mt-2">
            Vue d'ensemble des métriques de la plateforme
          </p>
        </header>

        {/* Main content */}
        <main>
          {/* Stats globales */}
          <section aria-labelledby="platform-stats-heading" className="mb-8">
            <h2 id="platform-stats-heading" className="sr-only">
              Statistiques de la plateforme
            </h2>
            {platformMetrics && <PlatformStats data={platformMetrics} />}
          </section>

          {/* Graphique activité - Temporairement désactivé */}
          {/* 
          <section aria-labelledby="activity-chart-heading" className="mb-8">
            <h2 id="activity-chart-heading" className="text-xl font-semibold mb-4 text-zinc-900 dark:text-white">
              Activité
            </h2>
            <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
              <ActivityChart timeRange={timeRange} onTimeRangeChange={setTimeRange} />
            </div>
          </section>
          */}

          {/* Répartition storage */}
          <section aria-labelledby="storage-heading" className="mb-8">
            <h2 id="storage-heading" className="text-xl font-semibold mb-4 text-zinc-900 dark:text-white">
              Répartition du stockage
            </h2>
            {storageData && <StorageBreakdown data={storageData} />}
          </section>

          {/* Résumé users */}
          <section aria-labelledby="users-heading">
            <h2 id="users-heading" className="text-xl font-semibold mb-4 text-zinc-900 dark:text-white">
              Utilisateurs
            </h2>
            {usersMetrics?.items && <UsersSummary data={usersMetrics.items} />}
          </section>
        </main>
      </div>
    </div>
  );
}
